<template>
  <div class="oai-LoadingStep">
    <div class="oai-LoadingStep_Header">
      <div class="oai-LoadingStep_LogoContainer">
        <AppLogo type="short" width="64" height="64" />
      </div>

      <i18n-t :keypath="titleKeypath" class="oai-LoadingStep_Title" tag="div">
        <template #accent>
          <span class="oai-LoadingStep_TitleAccent">{{ $t(titleAccentKey) }}</span>
        </template>
      </i18n-t>

      <div v-if="subtitleKey" class="oai-LoadingStep_Subtitle">
        {{ $t(subtitleKey) }}
      </div>
    </div>

    <OnboardingAiGradientProgressBar />

    <div class="oai-LoadingStep_Tasks">
      <div
        v-for="(task, index) in tasks"
        :key="task"
        class="oai-LoadingStep_Task"
        :class="{
          'oai-LoadingStep_Task-active': index === activeTaskIndex,
          'oai-LoadingStep_Task-done': index < activeTaskIndex
        }"
      >
        <span class="oai-LoadingStep_TaskIconWrap">
          <AppIcon
            class="oai-LoadingStep_TaskIcon"
            :class="{
              'oai-LoadingStep_TaskIcon-loading': taskIconNames[index] === 'logo-loader'
            }"
            height="24"
            :icon-name="taskIconNames[index]"
            width="24"
          />
        </span>

        <span class="oai-LoadingStep_TaskLabel">{{ $t(task) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, toRef } from 'vue'

import { useOnboardingAiLoadingStepTasks } from '@/composables/onboarding-ai-loading-step'
import {
  ONBOARDING_LOADING_VARIANT,
  useOnboardingAiStepContext
} from '@/composables/onboarding-ai-step'
import { getOnboardingAiLoadingStepVariantConfig } from '@/utils/onboarding-ai/loading-step-config'

import AppLogo from '@/components/AppLogo'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import OnboardingAiGradientProgressBar from '@/components/ui/onboarding-ai/OnboardingAiGradientProgressBar'

defineOptions({
  name: 'OnboardingAiLoadingStep'
})

const props = defineProps({
  variant: {
    type: String,
    required: true,
    validator: value => Object.values(ONBOARDING_LOADING_VARIANT).includes(value)
  },

  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits({
  complete: null
})

const stepContext = useOnboardingAiStepContext()
let loaderShownAt = null

onMounted(() => {
  loaderShownAt = Date.now()

  if (stepContext) {
    stepContext.loaderDurationMs = 0
  }
})

const handleComplete = () => {
  if (stepContext && loaderShownAt !== null) {
    stepContext.loaderDurationMs = Date.now() - loaderShownAt
  }

  emit('complete')
}

const variantConfig = computed(() => getOnboardingAiLoadingStepVariantConfig(props.variant))

const tasks = computed(() => variantConfig.value?.tasks ?? [])
const titleKeypath = computed(() => variantConfig.value?.titleKeypath)
const titleAccentKey = computed(() => variantConfig.value?.titleAccentKey)
const subtitleKey = computed(() => variantConfig.value?.subtitleKey)

const { activeTaskIndex, taskIconNames } = useOnboardingAiLoadingStepTasks({
  tasks,
  variantConfig,
  loading: toRef(props, 'loading'),
  onComplete: handleComplete
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/wizard-steps';
@import '~@/assets/styles/onboarding-ai-step';

.oai-LoadingStep {
  @extend %wizard-step-styles;

  --step-max-width: 480px;
  --step-gap: 24px;
  width: 100%;
  max-width: var(--step-max-width);
  margin-inline: auto;
}

.oai-LoadingStep_Header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.oai-LoadingStep_LogoContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: $border-radius-md;
  background: $white;
}

.oai-LoadingStep_Title {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  color: $oai-text-primary;

  font-size: 36px;
  font-style: normal;
  font-weight: fw('bold');
  line-height: 44px;
  letter-spacing: -0.72px;
}

.oai-LoadingStep_TitleAccent {
  font-family: $oai-font-family;
  font-size: 44px;
  font-style: italic;
  line-height: 40px;
  font-weight: fw('semi-bold');
  color: $oai-text-primary;
}

.oai-LoadingStep_Subtitle {
  font-size: $fs-14;
  line-height: 20px;
  color: $oai-text-muted;
}

.oai-LoadingStep_Tasks {
  display: grid;
  gap: 12px;
  list-style: none;
}

.oai-LoadingStep_Task {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: $fs-14;
  line-height: 20px;
  color: $oai-text-muted;
}

.oai-LoadingStep_Task-active,
.oai-LoadingStep_Task-done {
  color: $oai-text-body;
}

.oai-LoadingStep_TaskIconWrap {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.oai-LoadingStep_TaskIcon {
  flex-shrink: 0;
}

.oai-LoadingStep_TaskIcon-loading {
  animation: spin 1.2s linear infinite;
  transform-origin: center center;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
